import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { VOICE_MAX_SECONDS, VOICE_MIN_MS } from '@london-life/contracts';
import { env } from '../lib/env';

const clock = (ms: number) => {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};
// the format this browser records in: Opus in WebM (Chrome, Android, Firefox) or AAC in MP4 (Safari)
const recordingType = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const type of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'])
    if (MediaRecorder.isTypeSupported(type)) return type;
  return null;
};

// a voice note in a message: play and pause, with its length
export function VoicePlayer({ playPath, durationMs }: { playPath: string | null; durationMs: number }) {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [failed, setFailed] = useState(false);
  if (!playPath || failed)
    return (
      <span className="voice-player gone">🎤 {failed ? 'Could not play this voice note' : 'Voice note expired'}</span>
    );
  const toggle = () => {
    if (!audio.current) {
      const element = new Audio(`${env.apiOrigin}${playPath}`);
      element.ontimeupdate = () => setAt(element.currentTime * 1000);
      element.onended = () => (setPlaying(false), setAt(0));
      element.onerror = () => (setPlaying(false), setFailed(true));
      audio.current = element;
    }
    if (playing) {
      audio.current.pause();
      setPlaying(false);
    } else {
      void audio.current.play().then(
        () => setPlaying(true),
        () => setFailed(true),
      );
    }
  };
  const share = durationMs ? Math.min(100, (at / durationMs) * 100) : 0;
  return (
    <span className="voice-player">
      <button type="button" onClick={toggle} aria-label={playing ? 'Pause voice note' : 'Play voice note'}>
        {playing ? '❚❚' : '▶'}
      </button>
      <span className="voice-track" aria-hidden="true">
        <span style={{ width: `${share}%` }} />
      </span>
      <small>{clock(playing || at ? at : durationMs)}</small>
    </span>
  );
}

type Recording = { recorder: MediaRecorder; stream: MediaStream; started: number; chunks: Blob[]; type: string };

// Lagos Life's mic: hold to talk and let go to send, or tap for hands-free (then Send or ✕). A minute at most in
// Messages; Gist's voice notes run to two minutes and are put on the post when done.
export function VoiceButton({
  canSend,
  blockedText,
  disabled,
  onSend,
  onNote,
  maxSeconds = VOICE_MAX_SECONDS,
  doneLabel = 'Send',
}: {
  canSend: boolean;
  blockedText: string;
  disabled: boolean;
  onSend: (recording: Blob, type: string, ms: number) => Promise<boolean>;
  onNote: (text: string) => void;
  maxSeconds?: number;
  doneLabel?: string;
}) {
  const [recording, setRecording] = useState<Recording | null>(null);
  const [handsFree, setHandsFree] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pressedAt = useRef(0);
  // let go before the microphone was ready (a quick tap): hands-free once it is
  const letGo = useRef(false);
  const current = useRef<Recording | null>(null);
  current.current = recording;
  // never leave the microphone on
  useEffect(() => () => current.current?.stream.getTracks().forEach((track) => track.stop()), []);
  const start = async () => {
    const type = recordingType();
    if (!type || !navigator.mediaDevices?.getUserMedia) {
      onNote('This browser cannot record voice notes.');
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: type, audioBitsPerSecond: 32_000 });
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => event.data.size && chunks.push(event.data);
      recorder.start(250);
      setElapsed(0);
      setRecording({ recorder, stream, started: Date.now(), chunks, type });
      if (letGo.current) setHandsFree(true);
      return true;
    } catch {
      onNote('Allow the microphone to send voice notes.');
      return false;
    }
  };
  const finish = async (send: boolean) => {
    const active = current.current;
    if (!active) return;
    setRecording(null);
    setHandsFree(false);
    const ms = Date.now() - active.started;
    const stopped = new Promise<void>((resolve) => (active.recorder.onstop = () => resolve()));
    if (active.recorder.state !== 'inactive') active.recorder.stop();
    await stopped;
    active.stream.getTracks().forEach((track) => track.stop());
    if (!send) return;
    if (ms < VOICE_MIN_MS) {
      onNote('Hold to record a voice note.');
      return;
    }
    const base = active.type.split(';')[0]!;
    await onSend(new Blob(active.chunks, { type: base }), base, Math.min(ms, maxSeconds * 1000));
  };
  useEffect(() => {
    if (!recording) return;
    const timer = window.setInterval(() => {
      const ms = Date.now() - recording.started;
      setElapsed(ms);
      if (ms >= maxSeconds * 1000) void finish(true);
    }, 200);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);
  const down = (event: ReactPointerEvent) => {
    if (disabled || recording) return;
    if (!canSend) {
      onNote(blockedText);
      return;
    }
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pressedAt.current = Date.now();
    letGo.current = false;
    void start();
  };
  const up = () => {
    if (handsFree) return;
    if (!current.current) {
      letGo.current = true;
      return;
    }
    // a quick tap starts hands-free recording; a hold sends when let go
    if (Date.now() - pressedAt.current < 350) setHandsFree(true);
    else void finish(true);
  };
  if (recording && handsFree)
    return (
      <div className="voice-recording" role="group" aria-label="Recording a voice note">
        <button
          type="button"
          className="house-button quiet"
          aria-label="Cancel voice note"
          onClick={() => void finish(false)}
        >
          ✕
        </button>
        <span className="voice-live" aria-live="polite">
          <i aria-hidden="true" /> {clock(elapsed)} / {clock(maxSeconds * 1000)}
        </span>
        <button type="button" className="house-button save" onClick={() => void finish(true)}>
          {doneLabel}
        </button>
      </div>
    );
  return (
    <button
      type="button"
      className={recording ? 'voice-mic live' : canSend ? 'voice-mic' : 'voice-mic off'}
      aria-label="Record a voice note (hold, or tap for hands-free)"
      disabled={disabled}
      onPointerDown={down}
      onPointerUp={up}
      onPointerCancel={() => void finish(false)}
      onContextMenu={(event) => event.preventDefault()}
    >
      {recording ? clock(elapsed) : '🎤'}
    </button>
  );
}
